/**
 * Theme registry and `ThemeEngine` factory.
 *
 * The engine is a pure factory — there is no module-level singleton, so callers
 * (and tests) own the lifecycle. Behavior:
 *
 * - `list()` returns the registered manifests in registration order.
 * - `get(id)` looks one up by id, or `null`.
 * - `apply(id)` lazy-imports the new theme's eager art assets, writes color
 *   tokens to `:root`, notifies subscribers, and asynchronously persists the
 *   active theme id (Requirements 8.3, 8.4, 8.7, 11.5).
 * - `current()` returns the active manifest (defaults to `defaultId`, or the
 *   first manifest if `defaultId` is not registered).
 * - `onChange(cb)` registers a subscriber and returns an unsubscribe thunk.
 *
 * The default `applyCssVars` writes CSS custom properties to `:root`. It is a
 * no-op when `document` is not available, so the engine runs cleanly in
 * Node-based unit tests.
 */

import type { ThemeManifest } from './types';

/** Public engine surface consumed by the React state layer and theme picker. */
export interface ThemeEngine {
  /** Returns every registered manifest, in registration order. */
  list(): readonly ThemeManifest[];
  /** Returns the manifest with the given id, or `null` when not found. */
  get(id: string): ThemeManifest | null;
  /**
   * Switches to the theme with the given id.
   *
   * Throws when `id` is not registered. Lazy-loads the new theme's background
   * and character art, applies CSS custom properties, and notifies subscribers.
   * Persistence is fire-and-forget so it does not delay the visual swap.
   */
  apply(id: string): Promise<void>;
  /** Returns the currently active manifest. */
  current(): ThemeManifest;
  /** Registers a subscriber. Returns an unsubscribe thunk. */
  onChange(cb: (m: ThemeManifest) => void): () => void;
}

export interface CreateThemeEngineArgs {
  /** All registered theme manifests. */
  manifests: readonly ThemeManifest[];
  /** Initial theme id; falls back to `manifests[0]` when not registered. */
  defaultId: string;
  /**
   * Persist callback so the engine can save the active theme via the store.
   *
   * Called fire-and-forget on `apply` — rejections are caught so they never
   * surface as unhandled rejections, but they also do not block the swap.
   */
  persist?: (id: string) => Promise<void>;
  /**
   * Apply CSS custom properties to `:root`. Optional for non-DOM environments
   * (e.g. unit tests). Defaults to {@link defaultApplyCssVars} when omitted.
   */
  applyCssVars?: (manifest: ThemeManifest) => void;
}

/**
 * Default `applyCssVars`: writes the manifest's color tokens and font families
 * to `:root` as CSS custom properties.
 *
 * Skipped when `document` is undefined (e.g. SSR, Vitest's node environment).
 */
export function defaultApplyCssVars(manifest: ThemeManifest): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (!root) return;

  const { colors, typography } = manifest;
  const [gradStart, gradEnd] = colors.bgGradient;

  root.style.setProperty('--bg-gradient-start', gradStart);
  root.style.setProperty('--bg-gradient-end', gradEnd);
  root.style.setProperty('--surface', colors.surface);
  root.style.setProperty('--text-primary', colors.textPrimary);
  root.style.setProperty('--text-secondary', colors.textSecondary);
  root.style.setProperty('--accent-pink', colors.accentPink);
  root.style.setProperty('--accent-secondary', colors.accentSecondary);
  root.style.setProperty('--bubble-user', colors.bubbleUser);
  root.style.setProperty('--bubble-assistant', colors.bubbleAssistant);
  root.style.setProperty('--focus-ring', colors.focusRing);
  root.style.setProperty('--font-display', typography.display);
  root.style.setProperty('--font-body', typography.body);
}

/**
 * Create a fresh, isolated `ThemeEngine`.
 *
 * Pure factory — no module-level state. The engine owns its own subscriber set
 * and current-manifest pointer, so multiple engines can coexist (useful in
 * tests).
 */
export function createThemeEngine(args: CreateThemeEngineArgs): ThemeEngine {
  const { manifests, defaultId, persist, applyCssVars = defaultApplyCssVars } = args;

  if (manifests.length === 0) {
    throw new Error('createThemeEngine: at least one manifest is required');
  }

  // Resolve the initial manifest: defaultId if registered, else manifests[0].
  // The non-null assertion on manifests[0] is safe because we just guarded above.
  const initial =
    manifests.find((m) => m.id === defaultId) ?? (manifests[0] as ThemeManifest);

  let active: ThemeManifest = initial;
  const subscribers = new Set<(m: ThemeManifest) => void>();

  return {
    list(): readonly ThemeManifest[] {
      return manifests;
    },

    get(id: string): ThemeManifest | null {
      return manifests.find((m) => m.id === id) ?? null;
    },

    async apply(id: string): Promise<void> {
      const next = manifests.find((m) => m.id === id);
      if (!next) {
        throw new Error(
          `ThemeEngine.apply: theme "${id}" is not registered. ` +
            `Known ids: ${manifests.map((m) => m.id).join(', ')}`,
        );
      }

      // Eagerly lazy-load the new theme's art so the cross-fade has bytes to
      // draw (Requirement 11.5). Sound cues are optional and not blocking.
      await Promise.all([next.assets.backgroundArt(), next.assets.characterArt()]);

      // Apply color tokens to the DOM (Requirement 8.4). The custom hook lets
      // tests run without a `document` and lets non-DOM hosts swap behavior.
      applyCssVars(next);

      // Update internal state and notify subscribers synchronously so React
      // listeners see the change in the same tick the assets resolved.
      active = next;
      for (const cb of subscribers) cb(next);

      // Fire-and-forget persistence — the visual swap should not wait on
      // IndexedDB. Errors are swallowed so they never surface as unhandled
      // rejections; the next boot will simply restore the previous theme.
      if (persist) {
        void persist(id).catch(() => {
          /* persistence failure is non-fatal for the visual swap */
        });
      }
    },

    current(): ThemeManifest {
      return active;
    },

    onChange(cb: (m: ThemeManifest) => void): () => void {
      subscribers.add(cb);
      return () => {
        subscribers.delete(cb);
      };
    },
  };
}
